//------------CORE------------//
import React, { useState, useEffect } from "react";
//-------------MUI-----------//
import Card from "@mui/material/Card";
import GroupSharpIcon from "@mui/icons-material/GroupSharp";
import Zoom from "@mui/material/Zoom";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import { useGetBrandingQuery } from "services/branding";
//-------------MUI-ICONSS-----------//
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import WebhookIcon from "@mui/icons-material/Webhook";
import DeleteIcon from "@mui/icons-material/Delete";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import HandymanIcon from "@mui/icons-material/Handyman";
//-----------EXTERNAL LIBS----------//
import { useSnackbar } from "notistack";
//-----------EXTERNAL COMPONENTS---------//
import DeleteAlert from "components/Alerts/Delete";
import Loader from "components/Progress";
import Table from "components/Table/newTable";
import { AdminToolbar, AdminActions, RowAction, useBrowserTable, adminDateTime } from "components/Admin/adminTable";
import AddUsers from "./addUser";
import ImportUsers from "./importUsers";
import ApiKeyPopUp from "./apiKeysPopUp";
import {
  useGetUsersQuery,
  useDeleteUserMutation,
  useEditUserMutation,
  useGenerateApiKeysMutation,
} from "services/user";
import { useForgotPasswordMutation } from "services/auth";
import { useGetRolesQuery } from "services/roles";
import BrandingCard from "./Branding";
import { useSelector } from "react-redux";
import { roleFilterOptions } from "utils/userRoleOptions";

// the search box looks through these (the "Search by" picker can narrow it)
const searchFields = [
  { label: "Name", value: "name" },
  { label: "User Name", value: "userName" },
  { label: "Email", value: "email" },
  { label: "Role", value: "role" },
  { label: "Phone Number", value: "number" },
];

export default function UM(props) {
  let token = window.localStorage.getItem("token");
  const metaData = useSelector((state) => state.metaData);
  const allRoles = useGetRolesQuery(token);
  const [forgotPassword, resetResult] = useForgotPasswordMutation();
  const [deleteUser, deleteResult] = useDeleteUserMutation();
  const [openBranding, setOpenBranding] = React.useState(false);
  const users = useGetUsersQuery(
    { token, parameters: "" },
    { skip: !allRoles.isSuccess }
  );
  const { enqueueSnackbar } = useSnackbar();
  const [userId, setUserId] = useState("");
  const [rows, setRowState] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeUserName, setActiveUserName] = useState(null);
  const [deleteModal, setDelete] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]); // Item 3: filter users by role names ([] = all)
  const [search, setSearch] = useState("");
  const [pickedFields, setPickedFields] = useState(searchFields.map((f) => f.value));
  const [username, setUsername] = useState(null);
  const [block, setBlock] = useState(null);
  const [userEdit, setUserEdit] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [rolesIdMap, setRolesIdMap] = useState({});
  const [openKeysPopup, setOpenKeysPopup] = useState(false);
  const [apiKeysPopup, setApiKeysPopup] = useState({});
  const [isLoading,setIsLoading]=useState(true)

  const [editUser, updateResult] = useEditUserMutation();
  const [
    generateApiKeys,
    generateApiKeysResults,
  ] = useGenerateApiKeysMutation();

  const brandingRes = useGetBrandingQuery(
    { user: "true", id: userId },
    {
      skip: !openBranding,
    }
  );

  // console.log("USER CHANGED", userId);

  function ifLoaded(state, component) {
    if (state) return <Loader top="20vh" />;
    else return component();
  }

  useEffect(() => {
    var temp = [];
    if (users.isSuccess && users.data?.payload.Users) {
      users.data.payload.Users.forEach((elm) => {
        // Visibility (privilege-scoped): show only users whose role the caller fully holds
        // (same-or-lesser privilege), plus the caller's own row; a super admin sees all. This
        // works under READ/partial user-admin access too — `viewable` (BE) does NOT require
        // User Management ALL, unlike `editable` (which gates the row ACTIONS below). A more-
        // privileged user is never listed (privilege leak). Fall back to `editable` during the
        // deploy window if an older BE build hasn't shipped `viewable` yet.
        const isSelf = String(elm._id) === String(metaData?.userInfo?.userId);
        if (!(metaData?.unscopedAdmin || elm.viewable || elm.editable || isSelf))
          return;
        temp.push(
          createData(
            {
              // trimmed: an empty first or last name must not leave a space that sorts first
              name: `${elm.firstName ? elm.firstName : ""} ${
                elm.lastName ? elm.lastName : ""
              }`.trim(),
              status: elm?.currentLoggedIn,
              blocked: elm?.blocked,
            },
            elm.userName,
            rolesIdMap[elm.role],
            elm.email,
            elm.phone,
            {
              created: adminDateTime(elm?.createdAt),
              update: adminDateTime(elm?.updatedAt),
              login: adminDateTime(elm?.loginTime),
              logout: adminDateTime(elm?.logoutTime),
              loginAt: elm?.loginTime ? new Date(elm.loginTime).getTime() : 0,
            },
            elm
          )
        );
      });
      setRowState(temp);
      setIsLoading(false)
    }
  }, [users.isFetching, rolesIdMap]);

  useEffect(() => {
    if (users.isError) {
      // a failed read shows an empty table (not an endless spinner) and says why
      setIsLoading(false);
      showSnackbar("Users", users.error?.data?.message, "error", 1000);
    }
  }, [users.isError]);

  useEffect(() => {
    if (allRoles.isSuccess) {
      let idObj = {};
      let roles = allRoles.data.payload;
      roles.forEach((elm) => {
        idObj[elm._id] = elm.name;
      });
      setRolesIdMap(idObj);
      setRoles(roles);
    } else if (!allRoles.isFetching && allRoles.isError) {
      // without the roles the user list is never read: end the spinner and say why
      setIsLoading(false);
      showSnackbar("Roles", allRoles.error?.data?.message || "The roles could not be loaded", "error", 1000);
    }
  }, [allRoles.isFetching]);

  const handlePopupClose = () => {
    setUserEdit("");
    setOpenPopup(false);
  };

  async function onDelete(e, check) {
    let deletedUser = await deleteUser({ token, id: e, platformCheck: check });
    if (deletedUser.error) {
      showSnackbar("User", deletedUser.error?.data?.message, "error", 1000);
    } else {
      showSnackbar("User", deletedUser.data?.message, "success", 1000);
      toggleDelete();
    }
  }

  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar({ title, message, variant }, { timeOut });
  }

  async function onReset() {
    let forgot = await forgotPassword({ userName: username });
    if (forgot.data?.success) {
      showSnackbar(
        "User",
        `Verification email has been sent to ${username}`,
        "success",
        1000
      );
      setUsername(null);
    } else {
      showSnackbar("User", forgot.data?.message, "error", 1000);
    }
  }

  async function onBlock() {
    let updatedUser = await editUser({
      token,
      id: block.id,
      body: { blocked: !block.blocked },
    });
    if (updatedUser.error) {
      showSnackbar("User", updatedUser.error?.data?.message, "error", 1000);
    } else {
      showSnackbar("User", updatedUser.data?.message, "success", 1000);
      setBlock(null);
    }
  }

  async function onGenerateApiKeys(userId, email) {
    let apiKeys = await generateApiKeys({
      id: userId,
      email,
    });
    if (apiKeys.error) {
      showSnackbar("User", apiKeys.error?.data?.message, "error", 1000);
    } else {
      showSnackbar("User", apiKeys.data?.message, "success", 1000);
      setApiKeysPopup(apiKeys.data?.payload);
      setOpenKeysPopup(true);
    }
  }

  async function toggleDelete(id = null) {
    setActiveId(id);
    setDelete((state) => !state);
  }

  async function forgotPass(username = null) {
    setUsername(username);
  }

  async function blockUser(id = null, blocked = null) {
    setBlock({ id, blocked });
  }

  function columns() {
    let temp = [
      { id: "html2", label: "Name", align: "center" },
      // { id: "name", label: "Name", align: "center" },
      { id: "userName", label: "User Name", align: "center" },
      { id: "role", label: "Role", align: "center" },
      { id: "emailAddress", label: "Email", align: "center" },
      { id: "number", label: "Phone Number", align: "center" },
      { id: "html3", label: "Last Login", align: "center" },
    ];
    if (props.permission == "ALL")
      temp.push({
        id: "html",
        label: "Actions",
        align: "center",
        disableSorting: true,
      });
    return temp;
  }

  function createData(html2, userName, role, email, number, html3, html) {
    // `name` is a flat copy of the Name cell (html2.name) so free-text search can match
    // the leftmost column (the Table filters on top-level row keys).
    return { html2, userName, role, email, emailAddress: email, number, html3, html, name: html2?.name };
  }

  var html = (row) => {
    // S3 Fix-B: gate row admin actions on the BE-3 per-user `editable` hint (⊤ bypasses
    // via metaData.unscopedAdmin). editable already means "caller may administer this
    // user" (User Mgmt ALL + holds their role). The BE (checkUserEdit/Delete) is the
    // boundary; this pre-disables to avoid dead-end 403 clicks.
    const holdable = metaData?.unscopedAdmin || row.html.editable;
    // Item 1: API-key minting inherits the BE gate (checkApiUser). Now that every row is
    // visible, gate per-row so a partial admin can't mint a keypair for a broader-role
    // user (that key would authenticate AS the target). Self-service (own row) preserved.
    // D25 (a): nobody blocks or deletes their own user (grant-all included; the server
    // refuses it too), so Block and Delete are hidden on the caller's own row.
    const isSelf = String(row.html._id) === String(metaData?.userInfo?.userId);
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        {users.data.payload.apiAccess && (holdable || isSelf) && (
          <RowAction title="Generate API Keys" icon={<WebhookIcon />} onClick={() => onGenerateApiKeys(row.html._id, row.html.email)} />
        )}
        <RowAction
          title="Show Branding"
          icon={<ColorLensIcon />}
          onClick={() => {
            setActiveUserName(row.html.userName);
            setUserId(row.html._id);
            setOpenBranding(true);
          }}
        />
        {holdable && <RowAction title="Reset Password" icon={<VpnKeyIcon />} onClick={() => forgotPass(row.html.userName)} />}
        {holdable && !isSelf && (
          <RowAction
            title={row.html.blocked ? "Unblock" : "Block"}
            icon={row.html.blocked ? <LockOpenIcon /> : <LockIcon />}
            onClick={() => {
              setActiveUserName(row.html.userName);
              blockUser(row.html._id, row.html.blocked);
            }}
          />
        )}
        {holdable && (
          <RowAction
            title="Edit"
            icon={<EditIcon />}
            onClick={() => {
              setUserEdit(row.html);
              setOpenPopup(true);
            }}
          />
        )}
        {holdable && !isSelf && (
          <RowAction
            title="Delete"
            icon={<DeleteIcon />}
            onClick={() => {
              setActiveUserName(row.html.userName);
              toggleDelete(row.html._id);
            }}
          />
        )}
      </div>
    );
  };

  var html2 = (row) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            background: row.html2?.blocked
              ? "rgb(221,0,0,0.1)"
              : row.html2?.status
              ? "rgb(95,183,98,0.1)"
              : "rgb(85,85,106,0.1)",
            padding: "5px 8px",
            borderRadius: "10px",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              height: "10px",
              width: "10px",
              background: row.html2?.blocked
                ? "#dd0000"
                : row.html2?.status
                ? "#5fb762"
                : "grey",
              borderRadius: "50%",
            }}
          />
          <p
            style={{
              color: row.html2?.blocked
                ? "#dd0000"
                : row.html2?.status
                ? "#5fb762"
                : "grey",
              textTransform: "uppercase",
            }}
          >
            {row.html2.name}
          </p>
        </div>
      </div>
    );
  };

  var html3 = (row) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <p>{row.html3?.login}</p>
        <Tooltip
          title={
            <div>
              <p>Created: {row.html3?.created}</p>
              <p>Updated: {row.html3?.update}</p>
              <p>Login: {row.html3?.login}</p>
              <p>Logout: {row.html3?.logout}</p>
            </div>
          }
          placement="left"
          arrow
          TransitionComponent={Zoom}
        >
          <InfoOutlinedIcon style={{ color: "grey", cursor: "pointer" }} />
        </Tooltip>
      </div>
    );
  };

  // Item 3: role pre-filter (by resolved role name), applied before the search. D24: the choices
  // are the roles worn by the listed users; a remembered choice that no listed user wears any
  // more is dropped.
  const roleOptions = roleFilterOptions(rows);
  const activeRoles = roleFilter.filter((r) => roleOptions.includes(r));
  const table = useBrowserTable({
    rows: activeRoles.length ? rows.filter((r) => activeRoles.includes(r.role)) : rows,
    search,
    fields: { name: (r) => r.name, userName: (r) => r.userName, email: (r) => r.email, role: (r) => r.role, number: (r) => r.number },
    picked: pickedFields,
    sortable: {
      html2: (r) => r.name,
      userName: (r) => r.userName,
      role: (r) => r.role,
      emailAddress: (r) => r.email,
      number: (r) => r.number,
      html3: (r) => r.html3.loginAt,
    },
    defaultSort: { html2: 1 },
    resetKey: activeRoles,
  });

  function cardFunc() {
    return (
      <Table
        columns={columns()}
        rows={table.rows}
        html={html}
        html2={html2}
        html3={html3}
        page={table.page}
        totalPages={table.totalPages}
        totalDocuments={table.totalDocuments}
        handleChange={table.handleChange}
        pageSize={table.pageSize}
        setPageSize={table.setPageSize}
        pageSizeOptions={table.pageSizeOptions}
        sort={table.sort}
        setSort={table.setSort}
        height="calc(100vh - 280px)"
        minWidth="100%"
        overflowX="auto"
        // no side margins on the page-number row, or the table area scrolls sideways a second time
        paginationMargin="2vh 0"
      />
    );
  }

  return (
    <div>
      <Dialog open={openBranding} onClose={() => setOpenBranding(false)} PaperProps={{ style: { maxWidth: "90vw", maxHeight: "90vh", width: "500px" } }}>
        <DialogTitle style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {`Branding (${activeUserName})`}
        </DialogTitle>
        {brandingRes.isFetching ? (
          <Loader />
        ) : (
          <BrandingCard elm={brandingRes?.data?.payload?.branding} />
        )}
        <DialogActions>
          <Button onClick={() => setOpenBranding(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {props.permission == "ALL" ? (
        // the same "+" menu as Device Management: add one user, or upload a CSV file of users
        <AdminActions
          actions={[
            { name: "Add User", icon: <PersonAddIcon />, onClick: () => setOpenPopup(true) },
            { name: "Upload CSV", icon: <UploadFileIcon />, onClick: () => setOpenImport(true) },
          ]}
        />
      ) : null}
      <Card style={{ maxWidth: "calc(100vw - 90px)" }}>
        <AdminToolbar
          title="Users"
          icon={<GroupSharpIcon color="disabled" />}
          filters={
            // D24 (owner): the role filter is a type-ahead box; styled like the other filters and,
            // like Device Management's solution filter, it takes several roles (none = all roles)
            <div style={{ width: "20%", minWidth: 0 }}>
              <Autocomplete
                id="user-role-filter"
                multiple
                size="small"
                disableCloseOnSelect
                autoHighlight
                options={roleOptions}
                value={activeRoles}
                onChange={(e, picked) => setRoleFilter(picked)}
                renderOption={(optionProps, option, { selected }) => (
                  <li {...optionProps}>
                    <Checkbox size="small" checked={selected} />
                    {option}
                  </li>
                )}
                renderTags={(picked) => (
                  <span style={{ marginLeft: 8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {picked.length == 1 ? picked[0] : `${picked.length} roles`}
                  </span>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Filter By Role" placeholder={activeRoles.length ? "" : "All roles"} InputLabelProps={{ shrink: true }} />
                )}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", flexWrap: "nowrap" } }}
              />
            </div>
          }
          search={{
            value: search,
            onChange: setSearch,
            placeholder: "Search name, username, email, role, phone ...",
            fields: searchFields,
            picked: pickedFields,
            onPick: setPickedFields,
          }}
        />
        <div
          style={{
            margin: "20px",
            minHeight: "50vh",
          }}
        >
          {ifLoaded(isLoading, cardFunc)}
        </div>
      </Card>
      {activeId ? (
        <DeleteAlert
          deleteModal={deleteModal}
          title={`Delete (${activeUserName})`}
          question={`Are you sure you want to delete this user?`}
          adminStyle
          id={activeId}
          handleDelete={onDelete}
          handleClose={toggleDelete}
          deleteResult={deleteResult}
        />
      ) : null}
      {username ? (
        <DeleteAlert
          deleteModal={true}
          title={`Reset password (${username})`}
          question={`Are you sure you want to reset password for this user?`}
          adminStyle
          platformCheck={false}
          id={activeId}
          handleDelete={onReset}
          handleClose={() => setUsername(null)}
          deleteResult={resetResult}
        />
      ) : null}
      {block ? (
        <DeleteAlert
          deleteModal={true}
          title={`${block.blocked ? "Unblock" : "Block"} (${activeUserName})`}
          question={`Are you sure you want to ${
            block.blocked ? "unblock" : "block"
          } this user?`}
          platformCheck={false}
          id={activeId}
          handleDelete={onBlock}
          handleClose={() => setBlock(null)}
          deleteResult={updateResult}
          adminStyle
        />
      ) : null}
      {openPopup ? (
        <AddUsers
          userEdit={userEdit}
          roles={roles}
          license2FAEnabled={users.data?.payload?.license2FAEnabled}
          twoFAOptionEnabledForAdmins={
            users.data?.payload?.twoFAOptionEnabledForAdmins
          }
          handlePopupClose={handlePopupClose}
        />
      ) : null}
      {openImport ? (
        <ImportUsers
          onClose={() => setOpenImport(false)}
          tfaAllowed={!!(users.data?.payload?.license2FAEnabled && users.data?.payload?.twoFAOptionEnabledForAdmins)}
        />
      ) : null}
      {openKeysPopup ? (
        <ApiKeyPopUp
          handlePopupClose={() => {
            setOpenKeysPopup(false);
          }}
          apiKeys={apiKeysPopup}
        />
      ) : null}
    </div>
  );
}
